import { useState, useEffect } from "react";
import { PrinterIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import PageHeader from "../../components/layout/PageHeader";
import toast from "react-hot-toast";
import { reportsApi } from "../../api/reports";
import { activitiesApi } from "../../api/activities";
import { useSeason } from "../../context/SeasonContext";
import { formatCabinNumber } from "../../utils/terminology";
import { isCamperCabin } from "../../utils/cabinFilter";
import { sortAcs } from "../../utils/acSort";
import type { AcBookActivity, AcBookAc, Activity } from "../../types";

// ─── Print styles ─────────────────────────────────────────────────────────────

const PRINT_STYLE = `
.ac-cover-page { display: none; }
@media print {
  @page { size: letter portrait; margin: 0.5in; }
  .no-print { display: none !important; }
  .ac-cover-page { display: block !important; }
  .ac-book-page { break-after: page; page-break-after: always; border: none !important; box-shadow: none !important; margin-bottom: 0 !important; padding: 0 !important; }
  .ac-book-last { break-after: auto !important; page-break-after: auto !important; }
  .camper-block { margin-bottom: 0.3in; break-inside: avoid; page-break-inside: avoid; }
  .camper-block .ac-grid-table { width: 100%; table-layout: fixed; border-collapse: collapse; font-size: 9px; }
  .camper-block .ac-grid-table th, .camper-block .ac-grid-table td { border: 1px solid #777; padding: 2px 1px; }
  body { background: white !important; }
  .print-container { padding: 0 !important; }
}
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return `'${String(d.getFullYear()).slice(2)}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function AcCell({ completedDate }: { completedDate: string | null }) {
  if (completedDate) {
    return (
      <div className="flex items-center justify-center text-forest-700">
        <span className="tabular-nums text-sm">{formatDate(completedDate)}</span>
      </div>
    );
  }
  return null;
}

// ─── Cover pages ──────────────────────────────────────────────────────────────

function AcRow({ ac, muted }: { ac: AcBookAc; muted?: boolean }) {
  return (
    <div className={`py-2.5 border-b border-stone-200 last:border-0 ${muted ? "opacity-60" : ""}`}>
      <div className="flex items-baseline gap-3">
        <span className="font-bold text-stone-800 shrink-0 w-8">{ac.displayNumber}.</span>
        <span className="text-stone-700">{ac.name}</span>
      </div>
      {ac.description && (
        <p className="mt-0.5 text-sm text-stone-500 pl-[44px]">{ac.description}</p>
      )}
    </div>
  );
}

function CoverPageAcs({ activity }: { activity: AcBookActivity["activity"] }) {
  return (
    <div className="ac-cover-page ac-book-page mb-8 border border-stone-300 rounded-lg p-6">
      <div className="mb-4 pb-3 border-b-2 border-stone-800">
        <div className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-0.5">
          {activity.name}
        </div>
        <div className="text-2xl font-bold text-stone-900">Achievement Credits</div>
      </div>
      <div>
        {sortAcs(activity.achievementCredits).map((ac) => (
          <AcRow key={ac.id} ac={ac} />
        ))}
      </div>
    </div>
  );
}

function CoverPageInfo({ activity }: { activity: AcBookActivity["activity"] }) {
  const hasInfo = activity.description || activity.location;
  const hasArchived = activity.archivedAchievementCredits.length > 0;
  return (
    <div className="ac-cover-page ac-book-page mb-8 border border-stone-300 rounded-lg p-6">
      <div className="mb-4 pb-3 border-b-2 border-stone-800">
        <div className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-0.5">
          {activity.name}
        </div>
        <div className="text-2xl font-bold text-stone-900">Additional Information</div>
      </div>

      {hasInfo && (
        <div className="mb-6">
          {activity.location && (
            <div className="mb-2 text-sm">
              <span className="font-semibold text-stone-700">Location: </span>
              <span className="text-stone-600">{activity.location}</span>
            </div>
          )}
          {activity.description && (
            <p className="text-stone-600 whitespace-pre-wrap text-sm">{activity.description}</p>
          )}
        </div>
      )}

      {hasArchived && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-3">
            Retired / Previous Achievement Credits
          </div>
          {activity.archivedAchievementCredits.map((ac) => (
            <div key={ac.id} className="py-2.5 border-b border-stone-200 last:border-0 opacity-60">
              <div className="flex items-baseline gap-3">
                <span className="font-bold text-stone-600 shrink-0 w-8">#{ac.displayNumber}</span>
                <span className="text-stone-600">{ac.name}</span>
              </div>
              {ac.description && (
                <p className="mt-0.5 text-sm text-stone-400 pl-[44px]">{ac.description}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {!hasInfo && !hasArchived && (
        <p className="text-sm text-stone-400 italic">No additional information.</p>
      )}
    </div>
  );
}

function CabinPage({
  activityName,
  acs,
  cabinName,
  campers,
  isLast,
}: {
  activityName: string;
  acs: { id: number; name: string; code: string; displayNumber: number }[];
  cabinName: string;
  campers: AcBookActivity["cabins"][0]["campers"];
  isLast: boolean;
}) {
  return (
    <div className={`ac-book-page ${isLast ? "ac-book-last" : "mb-8"} border border-stone-300 rounded-lg p-6`}>
      {/* Page header */}
      <div className="mb-4 pb-3 border-b-2 border-stone-800">
        <div className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-0.5">
          {activityName}
        </div>
        <div className="text-2xl font-bold text-stone-900">Cabin: {cabinName}</div>
      </div>

      {/* Per-camper blocks */}
      <div className="space-y-6">
        {campers.map((camper) => (
          <div key={camper.id} className="camper-block">
            <div className="text-sm font-semibold text-stone-800 mb-1">
              {camper.lastName}, {camper.firstName}
            </div>
            <table className="ac-grid-table w-full table-fixed border-collapse border border-stone-400 text-xs">
              <thead>
                <tr>
                  {acs.map((ac) => (
                    <th
                      key={ac.id}
                      className="border border-stone-400 px-1 py-1 text-center font-semibold text-stone-700"
                      title={ac.name}
                    >
                      {ac.displayNumber}
                    </th>
                  ))}
                  <th className="border border-stone-400 px-1 py-1 text-center font-semibold text-stone-700 w-10">
                    Grad
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  {acs.map((ac) => {
                    const completion = camper.completions[ac.id];
                    return (
                      <td
                        key={ac.id}
                        className="border border-stone-400 px-1 text-center align-middle"
                        style={{ height: "44px" }}
                      >
                        <AcCell
                          completedDate={
                            completion?.status === "completed"
                              ? (completion.completedDate ?? null)
                              : null
                          }
                        />
                      </td>
                    );
                  })}
                  <td className="border border-stone-400 px-1 text-center align-middle" style={{ height: "44px" }}>
                    {camper.graduated && (
                      <CheckCircleIcon className="w-4 h-4 text-forest-700 mx-auto" />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AcBookReport() {
  const { selectedTermId, selectedYear, selectedTerm } = useSeason();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activityId, setActivityId] = useState<number | "all">("all");
  const [data, setData] = useState<AcBookActivity[]>([]);
  const [loading, setLoading] = useState(false);

  // Inject print CSS once
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = PRINT_STYLE;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // Load activities on mount
  useEffect(() => {
    activitiesApi
      .list()
      .then((raw) => setActivities([...raw].sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => toast.error("Failed to load activities"));
  }, []);

  // Fetch AC book data when term or activity selection changes
  useEffect(() => {
    if (selectedTermId === null) { setData([]); return; }
    setLoading(true);
    reportsApi
      .acBook(selectedTermId, activityId === "all" ? undefined : activityId)
      .then(setData)
      .catch(() => toast.error("Failed to load AC book data"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTermId, activityId]);

  // Page count: 2 cover pages per activity + one cabin page per cabin
  const totalPages = data.reduce((sum, entry) => sum + 2 + entry.cabins.length, 0);

  const termLabel = selectedTerm
    ? `${selectedTerm.year} — ${selectedTerm.name}`
    : `${selectedYear} — All Campers`;

  return (
    <div>
      <PageHeader
        title="AC Books"
        subtitle={termLabel ? `Print-ready binder pages for ${termLabel}` : "Print-ready binder pages"}
      />

      {/* Controls — hidden on print */}
      <div className="no-print flex flex-wrap items-center gap-4 mb-6 px-6">
        {/* Activity selector */}
        <div className="flex items-center gap-2">
          <label htmlFor="activity-select" className="text-sm font-medium text-stone-600">
            Activity
          </label>
          <select
            id="activity-select"
            value={activityId}
            onChange={(e) =>
              setActivityId(e.target.value === "all" ? "all" : Number(e.target.value))
            }
            className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
          >
            <option value="all">All Active Activities</option>
            {activities.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        {/* Print button */}
        <button
          onClick={() => window.print()}
          disabled={loading || totalPages === 0}
          className="flex items-center gap-1.5 rounded-md bg-forest-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-forest-700 focus:outline-none focus:ring-2 focus:ring-forest-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PrinterIcon className="w-4 h-4" />
          Print / Save as PDF
        </button>

        {!loading && totalPages > 0 && (
          <span className="text-sm text-stone-400">{totalPages} page{totalPages !== 1 ? "s" : ""}</span>
        )}
      </div>

      {/* Report content */}
      <div className="print-container px-6">
        {loading ? (
          <div className="no-print flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : totalPages === 0 ? (
          <div className="no-print text-center py-16 text-stone-500">
            No data found. Make sure campers are enrolled in this term and activities have ACs defined.
          </div>
        ) : (
          data.map((entry, entryIdx) => {
            const isLastEntry = entryIdx === data.length - 1;
            return (
              <div key={entry.activity.id}>
                <CoverPageAcs activity={entry.activity} />
                <CoverPageInfo activity={entry.activity} />
                {entry.cabins.filter((c) => isCamperCabin({ name: c.name })).map((cabin, cabinIdx) => (
                  <CabinPage
                    key={cabin.id}
                    activityName={entry.activity.name}
                    acs={entry.activity.achievementCredits}
                    cabinName={formatCabinNumber(cabin.name)}
                    campers={cabin.campers}
                    isLast={isLastEntry && cabinIdx === entry.cabins.length - 1}
                  />
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
